import React, { useState } from 'react';
import { coursesData } from '../../data/courses';

const CoursesSection = () => {
  const [activeCategory, setActiveCategory] = useState('advanced'); 
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFiltering, setIsFiltering] = useState(false);
  const [courseView, setCourseView] = useState('grid'); // 'grid' or 'list'
  
  // Define course categories
  const categories = [
    { id: 'all', name: 'All Courses' },
    { id: 'foundations', name: 'CS Foundations' },
    { id: 'advanced', name: 'Advanced CS' },
    { id: 'math', name: 'Mathematics' },
    { id: 'economics', name: 'Economics' },
    { id: 'english', name: 'English' },
    { id: 'psychology', name: 'Psychology' },
    { id: 'sociology', name: 'Sociology' },
    { id: 'other', name: 'Other' }
  ];
  
  // Complete course database with all 41 courses
  const coursesDataObj = {
    foundations: [
      {
        id: 'comp1001',
        code: 'COMP 1001',
        title: 'Intro to Programming',
        description: 'Introduction to fundamental programming concepts using Python. Covers variables, control structures, functions, and basic data structures.',
        semester: 'Fall 2021',
        skills: ['Python Programming', 'Problem Solving', 'Code Structure'],
        projects: ['Basic calculator', 'Text-based game'],
        color: '#3F51B5'
      },
      {
        id: 'comp1002',
        code: 'COMP 1002',
        title: 'Intro to Logic for Computer Science',
        description: 'Mathematical logic fundamentals including propositional logic, predicate logic, and their applications in computer science.',
        semester: 'Winter 2022',
        skills: ['Propositional Logic', 'Predicate Logic', 'Mathematical Reasoning'],
        projects: ['Logic proof system', 'Automated theorem checker'],
        color: '#00BCD4'
      },
      {
        id: 'comp1003',
        code: 'COMP 1003',
        title: 'Foundations of Computing Systems',
        description: 'Computer systems fundamentals including binary representation, computer architecture, and basic operating system concepts.',
        semester: 'Fall 2022',
        skills: ['Binary Systems', 'Computer Architecture', 'System Fundamentals'],
        projects: ['Binary calculator', 'Simple CPU simulator'],
        color: '#8BC34A'
      },
      {
        id: 'comp2001',
        code: 'COMP 2001',
        title: 'Object-Oriented Programming & HCI',
        description: 'Object-oriented programming principles in Java and introduction to user interface design and usability principles.',
        semester: 'Winter 2022',
        skills: ['Java Programming', 'OOP Concepts', 'UI Design'],
        projects: ['GUI application', 'Interactive user interface'],
        color: '#FF5722'
      },
      {
        id: 'comp2002',
        code: 'COMP 2002',
        title: 'Data Structures & Algorithms',
        description: 'Study of fundamental data structures and algorithms including arrays, linked lists, trees, sorting, and searching algorithms.',
        semester: 'Fall 2022',
        skills: ['Data Structures', 'Algorithm Design', 'Time Complexity'],
        projects: ['Data structure library', 'Algorithm visualization tool'],
        color: '#9C27B0'
      },
      {
        id: 'comp2003',
        code: 'COMP 2003',
        title: 'Computer Architecture',
        description: 'Detailed study of computer hardware including processor design, memory systems, and input/output mechanisms.',
        semester: 'Winter 2023',
        skills: ['Processor Design', 'Memory Systems', 'Hardware Architecture'],
        projects: ['CPU design project', 'Memory hierarchy analyzer'],
        color: '#607D8B'
      },
      {
        id: 'comp2004',
        code: 'COMP 2004',
        title: 'Intro to Operating Systems',
        description: 'Operating system concepts including process management, memory management, file systems, and system security.',
        semester: 'Fall 2023',
        skills: ['Process Management', 'Memory Management', 'File Systems'],
        projects: ['Simple shell implementation', 'Process scheduler'],
        color: '#E91E63'
      },
      {
        id: 'comp2005',
        code: 'COMP 2005',
        title: 'Software Engineering',
        description: 'Software development methodologies, project management, testing strategies, and software design principles.',
        semester: 'Winter 2023',
        skills: ['Software Design', 'Project Management', 'Testing Methodologies'],
        projects: ['Team software project', 'Testing framework'],
        color: '#FFC107'
      },
      {
        id: 'comp2006',
        code: 'COMP 2006',
        title: 'Computer Networking',
        description: 'Introduction to computer networks including protocols, network architecture, and distributed systems concepts.',
        semester: 'Fall 2023',
        skills: ['Network Protocols', 'Network Architecture', 'Distributed Systems'],
        projects: ['Network protocol implementation', 'Client-server application'],
        color: '#795548'
      },
      {
        id: 'comp2007',
        code: 'COMP 2007',
        title: 'Intro to Information Management',
        description: 'Database design, SQL, data modeling, and information system development principles.',
        semester: 'Winter 2024',
        skills: ['Database Design', 'SQL', 'Data Modeling'],
        projects: ['Database management system', 'Web-based data application'],
        color: '#009688'
      },
      {
        id: 'comp2008',
        code: 'COMP 2008',
        title: 'Social Issues & Professional Practice',
        description: 'Ethics in computing, professional responsibilities, and social impact of technology in modern society.',
        semester: 'Winter 2024',
        skills: ['Technology Ethics', 'Professional Practice', 'Social Impact Analysis'],
        projects: ['Ethics case study', 'Technology impact report'],
        color: '#FF9800'
      }
    ],
    advanced: [
      {
        id: 'comp3200',
        code: 'COMP 3200',
        title: 'Algorithmic Techniques for AI',
        description: 'Study of key algorithms and data structures used in artificial intelligence applications, including search, optimization, and learning methods.',
        semester: 'Fall 2024',
        skills: ['AI Algorithms', 'Heuristic Search', 'Optimization'],
        projects: ['Pathfinding visualizer', 'AI decision system'],
        color: '#9C27B0'
      },
      {
        id: 'comp3201',
        code: 'COMP 3201',
        title: 'Nature-Inspired Computing',
        description: 'Exploration of algorithms and computational methods inspired by natural processes and systems, including genetic algorithms, neural networks, and swarm intelligence.',
        semester: 'Fall 2023',
        skills: ['Genetic Algorithms', 'Neural Networks', 'Swarm Intelligence'],
        projects: ['Evolutionary optimization system', 'Bio-inspired problem solver'],
        color: '#50C878'
      },
      {
        id: 'comp3600',
        code: 'COMP 3600',
        title: 'Algorithm Design & Analysis',
        description: 'Advanced study of algorithm design strategies and analysis techniques. Topics include divide-and-conquer, dynamic programming, greedy algorithms, and NP-completeness.',
        semester: 'Winter 2023',
        skills: ['Algorithm Analysis', 'Problem Solving', 'Computational Complexity'],
        projects: ['Algorithmic solution visualizer', 'Performance analysis report'],
        color: '#4C75E0'
      },
      {
        id: 'comp3730',
        code: 'COMP 3730',
        title: 'Intro to Parallel Programming',
        description: 'Introduction to parallel computing architectures, programming models, and performance optimization for multi-core CPUs and GPUs.',
        semester: 'Fall 2024',
        skills: ['Parallel Algorithms', 'Multi-threading', 'Performance Optimization'],
        projects: ['Parallel sorting implementation', 'Multi-threaded application'],
        color: '#009688'
      },
      {
        id: 'comp4300',
        code: 'COMP 4300',
        title: 'Intro to Game Programming',
        description: 'Introduction to game architecture, collision detection, physics simulation, and game AI. Development of interactive games using modern engines.',
        semester: 'Fall 2023',
        skills: ['Game Mechanics', 'Physics Simulation', 'Interactive Design'],
        projects: ['2D platformer game', 'Physics-based puzzle game'],
        color: '#FF5252'
      },
      {
        id: 'comp4303',
        code: 'COMP 4303',
        title: 'AI in Computer Games',
        description: 'Study of artificial intelligence techniques for game agents, including decision making, planning, learning, and procedural content generation.',
        semester: 'Winter 2025',
        skills: ['Game AI', 'Agent Behavior', 'Procedural Generation'],
        projects: ['AI-driven NPC system', 'Procedural level generator'],
        color: '#673AB7'
      },
      {
        id: 'comp4304',
        code: 'COMP 4304',
        title: 'Data Visualization',
        description: 'Techniques and tools for effective visual representation of complex data, including principles of visual perception and interactive visualization.',
        semester: 'Winter 2025',
        skills: ['Visual Design', 'Interactive Graphics', 'Data Interpretation'],
        projects: ['Interactive data dashboard', 'Visual analytics system'],
        color: '#00BCD4'
      },
      {
        id: 'comp4750',
        code: 'COMP 4750',
        title: 'Intro to Natural Language Processing',
        description: 'Fundamentals of computational linguistics and text processing, including parsing, semantic analysis, and machine learning for language understanding.',
        semester: 'Fall 2023',
        skills: ['Text Processing', 'Language Models', 'Sentiment Analysis'],
        projects: ['Text analyzer application', 'Chatbot implementation'],
        color: '#E07A5F'
      },
      {
        id: 'comp4759',
        code: 'COMP 4759',
        title: 'Computer Networks',
        description: 'Advanced study of network protocols, routing algorithms, security mechanisms, and distributed systems architecture.',
        semester: 'Fall 2024',
        skills: ['Network Protocols', 'Routing', 'Distributed Systems'],
        projects: ['Protocol analyzer', 'Network simulation'],
        color: '#FF9800'
      },
      {
        id: 'comp4768',
        code: 'COMP 4768',
        title: 'Software Development for Mobile Devices',
        description: 'Design and implementation of applications for mobile platforms, covering UI/UX principles, data persistence, and device-specific APIs.',
        semester: 'Winter 2025',
        skills: ['Mobile Development', 'UI/UX Design', 'Cross-platform Tools'],
        projects: ['Full-featured mobile app', 'Cross-platform application'],
        color: '#4CAF50'
      },
      {
        id: 'comp499a',
        code: 'COMP 499A',
        title: 'Honours Research in Computer Science',
        description: 'Independent research project in computer science under faculty supervision, culminating in a thesis presentation and defense.',
        semester: 'Winter 2025',
        skills: ['Research Methods', 'Technical Writing', 'Advanced Topics'],
        projects: ['Research thesis', 'Conference-style paper'],
        color: '#2196F3'
      },
      {
        id: 'comp499b',
        code: 'COMP 499B',
        title: 'Honours Research in Computer Science',
        description: 'Continuation of independent research project in computer science, focusing on implementation and evaluation of research findings.',
        semester: 'Fall 2025',
        skills: ['Advanced Research', 'Implementation', 'Evaluation Methods'],
        projects: ['Research implementation', 'Thesis defense'],
        color: '#1976D2'
      }
    ],
    math: [
      {
        id: 'math1000',
        code: 'MATH 1000',
        title: 'Calculus I',
        description: 'Differential calculus including limits, derivatives, and applications to optimization and curve sketching.',
        semester: 'Fall 2021',
        skills: ['Differential Calculus', 'Limits', 'Optimization'],
        projects: ['Calculus visualization tool', 'Optimization problem solver'],
        color: '#3F51B5'
      },
      {
        id: 'math1001',
        code: 'MATH 1001',
        title: 'Calculus II',
        description: 'Integral calculus including techniques of integration, applications, and introduction to differential equations.',
        semester: 'Winter 2022',
        skills: ['Integral Calculus', 'Integration Techniques', 'Differential Equations'],
        projects: ['Integration calculator', 'Area calculation tool'],
        color: '#673AB7'
      },
      {
        id: 'math2000',
        code: 'MATH 2000',
        title: 'Calculus III',
        description: 'Multivariable calculus including partial derivatives, multiple integrals, and vector calculus.',
        semester: 'Winter 2023',
        skills: ['Multivariable Calculus', 'Vector Calculus', 'Multiple Integrals'],
        projects: ['3D function plotter', 'Vector field visualizer'],
        color: '#2196F3'
      },
      {
        id: 'math2050',
        code: 'MATH 2050',
        title: 'Linear Algebra I',
        description: 'Vector spaces, matrices, linear transformations, eigenvalues, and eigenvectors with computational applications.',
        semester: 'Fall 2022',
        skills: ['Matrix Operations', 'Vector Spaces', 'Linear Transformations'],
        projects: ['Matrix calculator', '3D transformation visualizer'],
        color: '#009688'
      },
      {
        id: 'stat2500',
        code: 'STAT 2500',
        title: 'Stats for Business & Arts',
        description: 'Statistical methods including probability distributions, hypothesis testing, and regression analysis.',
        semester: 'Fall 2023',
        skills: ['Statistical Analysis', 'Hypothesis Testing', 'Regression Analysis'],
        projects: ['Statistical analysis tool', 'Data visualization dashboard'],
        color: '#4CAF50'
      }
    ],
    economics: [
      {
        id: 'econ1010',
        code: 'ECON 1010',
        title: 'Intro to Microeconomics',
        description: 'Economic principles including supply and demand, market structures, and consumer behavior analysis.',
        semester: 'Fall 2022',
        skills: ['Economic Analysis', 'Market Theory', 'Consumer Behavior'],
        projects: ['Market simulation model', 'Economic data analysis'],
        color: '#FF5722'
      },
      {
        id: 'econ1020',
        code: 'ECON 1020',
        title: 'Intro to Macroeconomics',
        description: 'Macroeconomic principles including GDP, inflation, unemployment, and fiscal and monetary policy.',
        semester: 'Winter 2023',
        skills: ['Macroeconomic Theory', 'Policy Analysis', 'Economic Indicators'],
        projects: ['Economic policy analysis', 'Macroeconomic forecasting model'],
        color: '#F44336'
      }
    ],
    english: [
      {
        id: 'engl1090',
        code: 'ENGL 1090',
        title: 'Creative Reading & Writing: Telling Stories',
        description: 'Development of creative writing skills through analysis and creation of narrative structures and storytelling techniques.',
        semester: 'Fall 2021',
        skills: ['Creative Writing', 'Literary Analysis', 'Narrative Structure'],
        projects: ['Short story collection', 'Literary analysis paper'],
        color: '#FFC107'
      },
      {
        id: 'engl1110',
        code: 'ENGL 1110',
        title: 'Critical Reading & Writing in Rhetoric',
        description: 'Development of critical thinking and argumentative writing skills through rhetorical analysis and persuasive writing.',
        semester: 'Winter 2022',
        skills: ['Critical Writing', 'Rhetorical Analysis', 'Argumentative Writing'],
        projects: ['Rhetorical analysis essay', 'Persuasive writing portfolio'],
        color: '#FF9800'
      },
      {
        id: 'engl1192',
        code: 'ENGL 1192',
        title: 'CRW: Imagined Places',
        description: 'Exploration of creative writing through the lens of place and setting, including world-building and descriptive writing.',
        semester: 'Fall 2022',
        skills: ['World Building', 'Descriptive Writing', 'Setting Development'],
        projects: ['Creative place descriptions', 'World-building project'],
        color: '#FFB74D'
      }
    ],
    psychology: [
      {
        id: 'psyc1000',
        code: 'PSYC 1000',
        title: 'Intro to Psychology',
        description: 'Overview of psychological principles including cognitive psychology, behavioral science, and research methods.',
        semester: 'Winter 2022',
        skills: ['Psychological Principles', 'Research Methods', 'Behavioral Analysis'],
        projects: ['Psychology experiment design', 'Behavioral data analysis'],
        color: '#9C27B0'
      },
      {
        id: 'psyc1001',
        code: 'PSYC 1001',
        title: 'Intro to Psychology II',
        description: 'Continuation of introductory psychology covering developmental, social, and abnormal psychology.',
        semester: 'Fall 2022',
        skills: ['Developmental Psychology', 'Social Psychology', 'Abnormal Psychology'],
        projects: ['Development study', 'Social psychology experiment'],
        color: '#AB47BC'
      }
    ],
    sociology: [
      {
        id: 'soci1000',
        code: 'SOCI 1000',
        title: 'Introduction to Sociology',
        description: 'Fundamental concepts in sociology including social structures, institutions, and sociological research methods.',
        semester: 'Winter 2023',
        skills: ['Sociological Theory', 'Social Structure Analysis', 'Research Methods'],
        projects: ['Sociological research project', 'Social institution analysis'],
        color: '#795548'
      },
      {
        id: 'soci2120',
        code: 'SOCI 2120',
        title: 'Technology & Society',
        description: 'Sociological examination of technology\'s impact on society, digital culture, and technological determinism.',
        semester: 'Fall 2024',
        skills: ['Sociological Analysis', 'Technology Impact', 'Digital Culture'],
        projects: ['Technology impact study', 'Digital society research paper'],
        color: '#8D6E63'
      }
    ],
    other: [
      {
        id: 'gndr1000',
        code: 'GNDR 1000',
        title: 'Introduction to Gender Studies',
        description: 'Interdisciplinary exploration of gender theory, social construction of gender, and contemporary gender issues.',
        semester: 'Winter 2023',
        skills: ['Gender Theory', 'Social Analysis', 'Critical Thinking'],
        projects: ['Gender analysis research paper', 'Social issue presentation'],
        color: '#E91E63'
      },
      {
        id: 'hkr1000',
        code: 'HKR 1000',
        title: 'Fitness and Wellness',
        description: 'Comprehensive approach to physical fitness, health promotion, and wellness strategies for lifelong health.',
        semester: 'Fall 2021',
        skills: ['Fitness Planning', 'Health Education', 'Wellness Strategies'],
        projects: ['Personal fitness plan', 'Wellness program design'],
        color: '#4CAF50'
      },
      {
        id: 'mus2011',
        code: 'MUS 2011',
        title: 'North American Popular Music',
        description: 'Study of popular music genres, cultural contexts, and the evolution of North American popular music.',
        semester: 'Winter 2024',
        skills: ['Music Analysis', 'Cultural Context', 'Genre Studies'],
        projects: ['Music genre analysis', 'Cultural impact study'],
        color: '#FF4081'
      },
      {
        id: 'ocsc1000',
        code: 'OCSC 1000',
        title: 'Exploration of the World Ocean',
        description: 'Introduction to oceanography including marine ecosystems, ocean currents, and environmental impacts.',
        semester: 'Fall 2023',
        skills: ['Oceanography', 'Marine Biology', 'Environmental Science'],
        projects: ['Ocean current analysis', 'Marine ecosystem study'],
        color: '#00BCD4'
      },
      {
        id: 'easc2916',
        code: 'EASC 2916',
        title: 'Natural Hazards & the Dynamic Earth',
        description: 'Study of natural hazards including earthquakes, volcanoes, and extreme weather events and their impacts.',
        semester: 'Winter 2024',
        skills: ['Earth Science', 'Hazard Assessment', 'Risk Analysis'],
        projects: ['Hazard risk assessment', 'Earth science research project'],
        color: '#8BC34A'
      }
    ]
  };
  
  // Get all courses for 'all' category
  const allCourses = Object.values(coursesDataObj).flat();
  
  // Filter courses based on category and search term
  const filteredCourses = (() => {
    let courses;
    
    if (searchQuery) {
      courses = allCourses.filter(course => 
        course.code.toLowerCase().includes(searchQuery.toLowerCase()) || 
        course.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
    } else if (activeCategory === 'all') {
      courses = allCourses;
    } else {
      courses = coursesDataObj[activeCategory] || [];
    }
    
    return courses;
  })();
  
  // Handle search input
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsFiltering(query.length > 0);
  };
  
  // Handle category selection
  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setSelectedCourse(null);
    setSearchQuery('');
    setIsFiltering(false);
  };
  
  // Handle course selection
  const handleCourseClick = (courseId) => {
    setSelectedCourse(selectedCourse === courseId ? null : courseId);
  };
  
  // Get selected course data
  const getSelectedCourseData = () => {
    if (!selectedCourse) return null;
    return allCourses.find(course => course.id === selectedCourse);
  };
  
  // Toggle view mode
  const toggleView = () => {
    setCourseView(courseView === 'grid' ? 'list' : 'grid');
  };
  
  // Get category stats
  const getCategoryStats = () => {
    return {
      all: allCourses.length,
      foundations: coursesDataObj.foundations.length,
      advanced: coursesDataObj.advanced.length,
      math: coursesDataObj.math.length,
      economics: coursesDataObj.economics.length,
      english: coursesDataObj.english.length,
      psychology: coursesDataObj.psychology.length,
      sociology: coursesDataObj.sociology.length,
      other: coursesDataObj.other.length
    };
  };
  
  const stats = getCategoryStats();
  
  // Calculate the number of columns based on view mode
  const getColumnsPerRow = () => {
    if (courseView === 'list') return 1;
    // For grid view: 1 column on mobile, 2 on tablet, 3 on desktop
    // We'll use 3 as default for calculation
    return 3;
  };
  
  // Find which row the selected course is in
  const getSelectedCourseRowEnd = () => {
    if (!selectedCourse || courseView === 'list') return null;
    
    const selectedIndex = filteredCourses.findIndex(course => course.id === selectedCourse);
    if (selectedIndex === -1) return null;
    
    const columnsPerRow = getColumnsPerRow();
    const rowNumber = Math.floor(selectedIndex / columnsPerRow);
    const rowEndIndex = (rowNumber + 1) * columnsPerRow - 1;
    
    return Math.min(rowEndIndex, filteredCourses.length - 1);
  };
  
  const rowEndIndex = getSelectedCourseRowEnd();
  
  return (
    <div className="py-16 px-4 relative">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/5 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-20 -left-40 w-96 h-96 bg-purple-500/5 rounded-full filter blur-3xl"></div>
      </div>
      
      {/* Section header */}
      <div className="text-center mb-10 relative">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">Academic Coursework</h2>
        <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          Complete academic curriculum spanning computer science, mathematics, and interdisciplinary studies
        </p>
      </div>
      
      {/* Controls: search, category tabs, and view mode */}
      <div className="max-w-6xl mx-auto mb-6 flex flex-col gap-6">
        <div className="relative flex-grow">
          <input
            type="text"
            placeholder="Search courses..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full px-4 py-3 pl-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          {!isFiltering && (
            <div className="flex overflow-x-auto hide-scrollbar gap-2 pb-2 md:pb-0">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.id)}
                  className={`px-4 py-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors ${
                    activeCategory === category.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {category.name} ({stats[category.id] || 0})
                </button>
              ))}
            </div>
          )}
          
          <button
            onClick={toggleView}
            className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            {courseView === 'grid' ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            )}
          </button>
        </div>
      </div>
      
      {/* Courses display */}
      <div className="max-w-6xl mx-auto">
        {filteredCourses.length > 0 ? (
          <div className={courseView === 'grid' 
            ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" 
            : "space-y-4"
          }>
            {filteredCourses.map((course, index) => (
              <React.Fragment key={course.id}>
                {/* Course Card */}
                <div
                  onClick={() => handleCourseClick(course.id)}
                  className={`relative rounded-xl overflow-hidden transition-all cursor-pointer ${
                    courseView === 'grid'
                      ? 'bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 hover:shadow-lg hover:shadow-blue-500/10 hover:border-blue-500/50'
                      : 'bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 hover:border-blue-500/50 p-4 flex items-center'
                  } ${selectedCourse === course.id ? 'border-2 border-blue-500 dark:border-blue-500' : ''}`}
                >
                  {courseView === 'grid' ? (
                    <>
                      <div 
                        className="h-2 w-full" 
                        style={{ backgroundColor: course.color }}
                      ></div>
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{course.code}</h3>
                            <p className="text-gray-600 dark:text-gray-400">{course.title}</p>
                          </div>
                        </div>
                        
                        {course.semester && (
                          <div className="text-sm text-gray-500 dark:text-gray-500 mb-2">
                            {course.semester}
                          </div>
                        )}
                        
                        {course.description && (
                          <p className="text-gray-700 dark:text-gray-300 text-sm mb-4 line-clamp-2">
                            {course.description}
                          </p>
                        )}
                        
                        {course.skills && (
                          <div className="flex flex-wrap gap-1 mt-auto">
                            {course.skills.slice(0, 3).map((skill, idx) => (
                              <span 
                                key={idx} 
                                className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded text-xs"
                              >
                                {skill}
                              </span>
                            ))}
                            {course.skills.length > 3 && (
                              <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded text-xs">
                                +{course.skills.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div 
                        className="w-4 h-full min-h-[60px] rounded-l mr-4 flex-shrink-0" 
                        style={{ backgroundColor: course.color }}
                      ></div>
                      <div className="flex-grow">
                        <div className="flex justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{course.code}</h3>
                            <p className="text-gray-600 dark:text-gray-400">{course.title}</p>
                          </div>
                        </div>
                        
                        {course.semester && (
                          <div className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                            {course.semester}
                          </div>
                        )}
                      </div>
                      <div className="ml-2">
                        <svg 
                          className={`w-5 h-5 text-gray-400 transition-transform ${selectedCourse === course.id ? 'rotate-180' : ''}`} 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Course Details Panel - appears after the row ends */}
                {courseView === 'grid' && selectedCourse && index === rowEndIndex && (
                  <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                    {(() => {
                      const courseData = getSelectedCourseData();
                      if (!courseData || !courseData.description) return null;
                      
                      return (
                        <div className="rounded-xl overflow-hidden bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 transition-all animate-fadeIn">
                          <div className="relative">
                            <div 
                              className="h-3 w-full" 
                              style={{ backgroundColor: courseData.color }}
                            ></div>
                            <button
                              onClick={() => setSelectedCourse(null)}
                              className="absolute top-4 right-4 p-1 rounded-full bg-gray-100/80 dark:bg-gray-800/80 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                            
                            <div className="p-6">
                              <div className="flex flex-col md:flex-row justify-between mb-6">
                                <div>
                                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{courseData.code}</h3>
                                  <p className="text-xl text-gray-600 dark:text-gray-400">{courseData.title}</p>
                                  <div className="flex items-center mt-2">
                                    <span className="text-sm text-gray-500 dark:text-gray-500">{courseData.semester}</span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="mb-6">
                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Course Overview</h4>
                                <p className="text-gray-700 dark:text-gray-300">
                                  {courseData.description}
                                </p>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Skills & Techniques</h4>
                                  <div className="flex flex-wrap gap-2">
                                    {courseData.skills.map((skill, idx) => (
                                      <span 
                                        key={idx} 
                                        className="px-3 py-1 bg-gray-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                                      >
                                        {skill}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                
                                <div>
                                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Key Projects</h4>
                                  <ul className="space-y-2">
                                    {courseData.projects.map((project, idx) => (
                                      <li key={idx} className="flex items-start">
                                        <svg className="w-5 h-5 text-blue-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="text-gray-700 dark:text-gray-300">{project}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
                
                {/* For list view, show details immediately after the selected course */}
                {courseView === 'list' && selectedCourse === course.id && (
                  <div className="w-full">
                    {(() => {
                      const courseData = getSelectedCourseData();
                      if (!courseData || !courseData.description) return null;
                      
                      return (
                        <div className="rounded-xl overflow-hidden bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 transition-all animate-fadeIn mt-4">
                          <div className="relative">
                            <div 
                              className="h-3 w-full" 
                              style={{ backgroundColor: courseData.color }}
                            ></div>
                            
                            <div className="p-6">
                              <div className="mb-6">
                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Course Overview</h4>
                                <p className="text-gray-700 dark:text-gray-300">
                                  {courseData.description}
                                </p>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Skills & Techniques</h4>
                                  <div className="flex flex-wrap gap-2">
                                    {courseData.skills.map((skill, idx) => (
                                      <span 
                                        key={idx} 
                                        className="px-3 py-1 bg-gray-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                                      >
                                        {skill}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                
                                <div>
                                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Key Projects</h4>
                                  <ul className="space-y-2">
                                    {courseData.projects.map((project, idx) => (
                                      <li key={idx} className="flex items-start">
                                        <svg className="w-5 h-5 text-blue-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="text-gray-700 dark:text-gray-300">{project}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="text-center p-12 border border-gray-200 dark:border-gray-700 rounded-lg">
            <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl text-gray-600 dark:text-gray-400">No courses match your search</h3>
            <button 
              onClick={() => {setSearchQuery(""); setIsFiltering(false);}}
              className="mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
            >
              Clear search
            </button>
          </div>
        )}
      </div>
      
      {/* Course statistics - moved below the courses and details */}
      <div className="max-w-6xl mx-auto mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-lg p-6 flex flex-col items-center justify-center">
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">{stats.advanced}</div>
          <div className="text-gray-600 dark:text-gray-400 text-center">Advanced CS</div>
        </div>
        <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-lg p-6 flex flex-col items-center justify-center">
          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">{stats.foundations}</div>
          <div className="text-gray-600 dark:text-gray-400 text-center">CS Foundations</div>
        </div>
        <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-lg p-6 flex flex-col items-center justify-center">
          <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">{stats.math}</div>
          <div className="text-gray-600 dark:text-gray-400 text-center">Mathematics</div>
        </div>
        <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-lg p-6 flex flex-col items-center justify-center">
          <div className="text-3xl font-bold text-amber-600 dark:text-amber-400 mb-2">{stats.all}</div>
          <div className="text-gray-600 dark:text-gray-400 text-center">Total Courses</div>
        </div>
      </div>
      
      {/* Subject breakdown */}
      <div className="max-w-6xl mx-auto mt-8">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 text-center">Course Distribution by Subject</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-lg p-4 text-center">
            <div className="text-lg font-bold text-orange-600 dark:text-orange-400">{stats.economics}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">Economics</div>
          </div>
          <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-lg p-4 text-center">
            <div className="text-lg font-bold text-yellow-600 dark:text-yellow-400">{stats.english}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">English</div>
          </div>
          <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-lg p-4 text-center">
            <div className="text-lg font-bold text-pink-600 dark:text-pink-400">{stats.psychology}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">Psychology</div>
          </div>
          <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-lg p-4 text-center">
            <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{stats.sociology}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">Sociology</div>
          </div>
          <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-lg p-4 text-center">
            <div className="text-lg font-bold text-teal-600 dark:text-teal-400">{stats.other}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">Other</div>
          </div>
        </div>
      </div>
      
      {/* Add keyframes for animations */}
      <style>
        {`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        
        .line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        `}
      </style>
    </div>
  );
};

export default CoursesSection;